import { Injectable, ElementRef, Renderer2, RendererFactory2, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FocusTrapConfig {
	id: string;
	element: HTMLElement;
	autoFocus?: boolean;
	restoreFocus?: boolean;
}

export interface AnnouncementOptions {
	priority?: 'polite' | 'assertive';
	delay?: number;
	clearAfter?: number;
}

@Injectable({
	providedIn: 'root',
})
export class AccessibilityFocusService {
	private renderer: Renderer2;
	private isBrowser: boolean;

	// Focus trap management
	private focusTraps = new Map<string, FocusTrapConfig>();
	private previouslyFocusedElements = new Map<string, HTMLElement>();

	// Screen reader announcements
	private announcerId = 'bsg-sr-announcer';
	private announcer: HTMLElement | null = null;

	// High contrast detection
	private highContrastMode$ = new BehaviorSubject<boolean>(false);

	// Reduced motion detection
	private reducedMotion$ = new BehaviorSubject<boolean>(false);

	// Focus visible tracking
	private focusVisible$ = new BehaviorSubject<boolean>(false);

	constructor(
		private rendererFactory: RendererFactory2,
		@Inject(PLATFORM_ID) private platformId: Object
	) {
		this.renderer = this.rendererFactory.createRenderer(null, null);
		this.isBrowser = isPlatformBrowser(this.platformId);
	}

	/**
	 * Initialize accessibility features
	 */
	private initializeAccessibilityFeatures(): void {
		// Create screen reader announcer
		this.createScreenReaderAnnouncer();

		// Detect high contrast mode
		this.detectHighContrastMode();

		// Detect reduced motion preference
		this.detectReducedMotion();

		// Set up focus visible detection
		this.setupFocusVisibleDetection();

		// Listen for accessibility preference changes
		this.setupMediaQueryListeners();
	}

	/**
	 * Create focus trap for a given element
	 */
	createFocusTrap(elementRef: ElementRef, id: string, config: Partial<FocusTrapConfig> = {}): void {
		if (!this.isBrowser || !elementRef?.nativeElement) return;

		const element = elementRef.nativeElement;

		// Store previously focused element
		if (config.restoreFocus !== false) {
			this.previouslyFocusedElements.set(id, document.activeElement as HTMLElement);
		}

		// Create trap configuration
		const trapConfig: FocusTrapConfig = {
			id,
			element,
			autoFocus: config.autoFocus !== false,
			restoreFocus: config.restoreFocus !== false,
			...config,
		};

		this.focusTraps.set(id, trapConfig);

		// Set up event listeners
		this.setupFocusTrapListeners(trapConfig);

		// Auto focus if enabled
		if (trapConfig.autoFocus) {
			this.focusFirstElement(element);
		}
	}

	/**
	 * Destroy focus trap and restore focus
	 */
	destroyFocusTrap(id: string): void {
		if (!this.isBrowser) return;

		const trapConfig = this.focusTraps.get(id);
		if (!trapConfig) return;

		// Remove event listeners
		this.removeFocusTrapListeners(trapConfig);

		// Restore focus
		if (trapConfig.restoreFocus) {
			const previousElement = this.previouslyFocusedElements.get(id);
			if (previousElement && document.contains(previousElement)) {
				setTimeout(() => {
					previousElement.focus();
				}, 0);
			}
			this.previouslyFocusedElements.delete(id);
		}

		// Clean up
		this.focusTraps.delete(id);
	}

	/**
	 * Announce message to screen readers
	 */
	announceToScreenReader(message: string, options: AnnouncementOptions = {}): void {
		if (!this.isBrowser || !message.trim()) return;

		const { priority = 'polite', delay = 100, clearAfter = 3000 } = options;

		if (!this.announcer) {
			this.createScreenReaderAnnouncer();
		}

		if (this.announcer) {
			// Set priority
			this.announcer.setAttribute('aria-live', priority);

			// Clear existing content
			this.announcer.textContent = '';

			// Set new message after delay
			setTimeout(() => {
				if (this.announcer) {
					this.announcer.textContent = message;
				}
			}, delay);

			// Clear message after specified time
			if (clearAfter > 0) {
				setTimeout(() => {
					if (this.announcer) {
						this.announcer.textContent = '';
					}
				}, delay + clearAfter);
			}
		}
	}

	/**
	 * Get focusable elements within a container
	 */
	getFocusableElements(container: HTMLElement): HTMLElement[] {
		if (!this.isBrowser || !container) return [];

		const focusableSelector = [
			'button:not([disabled])',
			'[href]',
			'input:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
			'[contenteditable="true"]',
		].join(', ');

		return Array.from(container.querySelectorAll(focusableSelector)).filter(el =>
			this.isElementVisible(el as HTMLElement)
		) as HTMLElement[];
	}

	/**
	 * Focus first focusable element in container
	 */
	focusFirstElement(container: HTMLElement): boolean {
		const focusableElements = this.getFocusableElements(container);
		if (focusableElements.length > 0) {
			focusableElements[0].focus();
			return true;
		}
		return false;
	}

	/**
	 * Focus last focusable element in container
	 */
	focusLastElement(container: HTMLElement): boolean {
		const focusableElements = this.getFocusableElements(container);
		if (focusableElements.length > 0) {
			focusableElements[focusableElements.length - 1].focus();
			return true;
		}
		return false;
	}

	/**
	 * Handle keyboard navigation for lists/grids
	 */
	handleListNavigation(
		event: KeyboardEvent,
		elements: HTMLElement[],
		currentIndex: number,
		options: {
			orientation?: 'horizontal' | 'vertical' | 'both';
			wrap?: boolean;
			columns?: number;
		} = {}
	): number {
		const { orientation = 'vertical', wrap = true, columns = 1 } = options;
		let newIndex = currentIndex;

		switch (event.key) {
			case 'ArrowDown':
				if (orientation === 'vertical' || orientation === 'both') {
					newIndex =
						columns === 1
							? this.getNextIndex(currentIndex, elements.length, wrap)
							: Math.min(currentIndex + columns, elements.length - 1);
					event.preventDefault();
				}
				break;

			case 'ArrowUp':
				if (orientation === 'vertical' || orientation === 'both') {
					newIndex =
						columns === 1 ? this.getPreviousIndex(currentIndex, elements.length, wrap) : Math.max(currentIndex - columns, 0);
					event.preventDefault();
				}
				break;

			case 'ArrowRight':
				if (orientation === 'horizontal' || orientation === 'both') {
					newIndex = this.getNextIndex(currentIndex, elements.length, wrap);
					event.preventDefault();
				}
				break;

			case 'ArrowLeft':
				if (orientation === 'horizontal' || orientation === 'both') {
					newIndex = this.getPreviousIndex(currentIndex, elements.length, wrap);
					event.preventDefault();
				}
				break;

			case 'Home':
				newIndex = 0;
				event.preventDefault();
				break;

			case 'End':
				newIndex = elements.length - 1;
				event.preventDefault();
				break;
		}

		if (newIndex !== currentIndex && elements[newIndex]) {
			elements[newIndex].focus();
		}

		return newIndex;
	}

	/**
	 * Check if high contrast mode is enabled
	 */
	isHighContrastMode(): Observable<boolean> {
		return this.highContrastMode$.asObservable();
	}

	/**
	 * Check if reduced motion is preferred
	 */
	isReducedMotion(): Observable<boolean> {
		return this.reducedMotion$.asObservable();
	}

	/**
	 * Check if focus should be visible (keyboard navigation)
	 */
	isFocusVisible(): Observable<boolean> {
		return this.focusVisible$.asObservable();
	}

	/**
	 * Add keyboard event listener with cleanup
	 */
	addKeyboardListener(element: HTMLElement, callback: (event: KeyboardEvent) => void): () => void {
		if (!this.isBrowser) return () => {};

		const listener = (event: Event) => callback(event as KeyboardEvent);
		element.addEventListener('keydown', listener);

		return () => {
			element.removeEventListener('keydown', listener);
		};
	}

	/**
	 * Manage ARIA attributes dynamically
	 */
	setAriaAttribute(element: HTMLElement, attribute: string, value: string | null): void {
		if (!this.isBrowser || !element) return;

		if (value === null || value === undefined) {
			element.removeAttribute(`aria-${attribute}`);
		} else {
			element.setAttribute(`aria-${attribute}`, value);
		}
	}

	/**
	 * Private helper methods
	 */
	private createScreenReaderAnnouncer(): void {
		if (!this.isBrowser || this.announcer) return;

		this.announcer = this.renderer.createElement('div');
		this.renderer.setAttribute(this.announcer, 'id', this.announcerId);
		this.renderer.setAttribute(this.announcer, 'aria-live', 'polite');
		this.renderer.setAttribute(this.announcer, 'aria-atomic', 'true');
		this.renderer.addClass(this.announcer, 'visually-hidden');

		// Add CSS for screen reader only content
		this.renderer.setStyle(this.announcer, 'position', 'absolute');
		this.renderer.setStyle(this.announcer, 'width', '1px');
		this.renderer.setStyle(this.announcer, 'height', '1px');
		this.renderer.setStyle(this.announcer, 'margin', '-1px');
		this.renderer.setStyle(this.announcer, 'padding', '0');
		this.renderer.setStyle(this.announcer, 'overflow', 'hidden');
		this.renderer.setStyle(this.announcer, 'clip', 'rect(0, 0, 0, 0)');
		this.renderer.setStyle(this.announcer, 'white-space', 'nowrap');
		this.renderer.setStyle(this.announcer, 'border', '0');

		this.renderer.appendChild(document.body, this.announcer);
	}

	private setupFocusTrapListeners(config: FocusTrapConfig): void {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Tab') {
				this.handleTabKey(event, config);
			} else if (event.key === 'Escape') {
				// Optional: close trap on escape
				// this.destroyFocusTrap(config.id);
			}
		};

		config.element.addEventListener('keydown', handleKeyDown);
		(config as any).keyDownListener = handleKeyDown;
	}

	private removeFocusTrapListeners(config: FocusTrapConfig): void {
		if ((config as any).keyDownListener) {
			config.element.removeEventListener('keydown', (config as any).keyDownListener);
		}
	}

	private handleTabKey(event: KeyboardEvent, config: FocusTrapConfig): void {
		const focusableElements = this.getFocusableElements(config.element);
		if (focusableElements.length === 0) return;

		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		if (event.shiftKey) {
			// Shift + Tab
			if (document.activeElement === firstElement) {
				lastElement.focus();
				event.preventDefault();
			}
		} else {
			// Tab
			if (document.activeElement === lastElement) {
				firstElement.focus();
				event.preventDefault();
			}
		}
	}

	private isElementVisible(element: HTMLElement): boolean {
		return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
	}

	private detectHighContrastMode(): void {
		if (!this.isBrowser) return;

		const checkHighContrast = () => {
			// Check for forced-colors mode (Windows High Contrast)
			const forcedColors = window.matchMedia('(forced-colors: active)').matches;

			// Check for high contrast media query (older browsers)
			const highContrast = window.matchMedia('(-ms-high-contrast: active)').matches;

			const isHighContrast = forcedColors || highContrast;

			this.highContrastMode$.next(isHighContrast);

			// Add class to document root
			if (isHighContrast) {
				document.documentElement.classList.add('bsg-high-contrast');
			} else {
				document.documentElement.classList.remove('bsg-high-contrast');
			}
		};

		checkHighContrast();
	}

	private detectReducedMotion(): void {
		if (!this.isBrowser) return;

		const checkReducedMotion = () => {
			const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			this.reducedMotion$.next(reducedMotion);

			// Add class to document root
			if (reducedMotion) {
				document.documentElement.classList.add('bsg-reduced-motion');
			} else {
				document.documentElement.classList.remove('bsg-reduced-motion');
			}
		};

		checkReducedMotion();
	}

	private setupFocusVisibleDetection(): void {
		if (!this.isBrowser) return;

		let hadKeyboardEvent = false;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.altKey || event.ctrlKey) return;
			hadKeyboardEvent = true;
		};

		const handleFocus = () => {
			if (hadKeyboardEvent) {
				this.focusVisible$.next(true);
				document.documentElement.classList.add('bsg-focus-visible');
			}
		};

		const handleBlur = () => {
			this.focusVisible$.next(false);
			document.documentElement.classList.remove('bsg-focus-visible');
		};

		const handleMouseDown = () => {
			hadKeyboardEvent = false;
		};

		document.addEventListener('keydown', handleKeyDown, true);
		document.addEventListener('focus', handleFocus, true);
		document.addEventListener('blur', handleBlur, true);
		document.addEventListener('mousedown', handleMouseDown, true);
	}

	private setupMediaQueryListeners(): void {
		if (!this.isBrowser) return;

		// High contrast mode listener
		const highContrastQuery = window.matchMedia('(forced-colors: active)');
		const legacyHighContrastQuery = window.matchMedia('(-ms-high-contrast: active)');

		const handleHighContrastChange = () => this.detectHighContrastMode();

		if (highContrastQuery.addEventListener) {
			highContrastQuery.addEventListener('change', handleHighContrastChange);
		} else {
			// Fallback for older browsers
			highContrastQuery.addListener(handleHighContrastChange);
		}

		if (legacyHighContrastQuery.addEventListener) {
			legacyHighContrastQuery.addEventListener('change', handleHighContrastChange);
		} else {
			legacyHighContrastQuery.addListener(handleHighContrastChange);
		}

		// Reduced motion listener
		const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		const handleReducedMotionChange = () => this.detectReducedMotion();

		if (reducedMotionQuery.addEventListener) {
			reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
		} else {
			reducedMotionQuery.addListener(handleReducedMotionChange);
		}
	}

	private getNextIndex(currentIndex: number, length: number, wrap: boolean): number {
		if (currentIndex < length - 1) {
			return currentIndex + 1;
		}
		return wrap ? 0 : currentIndex;
	}

	private getPreviousIndex(currentIndex: number, length: number, wrap: boolean): number {
		if (currentIndex > 0) {
			return currentIndex - 1;
		}
		return wrap ? length - 1 : currentIndex;
	}
}
